import React, { useState } from 'react'
import { Avatar, Box, ListItem, ListItemAvatar, ListItemText, Popover, DialogContent, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAuthStore } from 'context/AuthStore'
import { getFomattedDate } from '../../lib/utils'
import FieldInfoNG from './FieldInfoNG'

const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer'
}))

const StyledPrimaryTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14)
}))

/**
 * Компонент отображает аватар, имя и телефон пользователя.
 * При клике открывает поповер с подробной информацией переданными через пропсы.
 * props:
 *  - avatar: URL аватара
 *  - fullName: полное имя
 *  - phone: телефон
 *  - details: React-элемент или массив элементов для отображения в поповере
 */
const UserInfo = ({ avatar, fullName = '', phone = '', details, address }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const { authStore } = useAuthStore()

  const handleOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'user-info-popover' : undefined

  return (
    <>
      <StyledListItem onClick={handleOpen} alignItems="center" disableGutters>
        <ListItemAvatar>
          <Avatar alt={`user_avatar_${fullName}`} src={avatar} />
        </ListItemAvatar>
        <ListItemText
          primary={<StyledPrimaryTypography variant="subtitle1">{fullName}</StyledPrimaryTypography>}
          secondary={
            <Typography component="span" variant="body2" color="text.secondary">
              {phone}
            </Typography>
          }
        />
      </StyledListItem>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <DialogContent sx={{ p: 2, m: 0 }}>
          <Box
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              borderRadius: 2,
              flexDirection: authStore.isMobile ? 'column' : 'row',
              maxHeight: authStore.isMobile ? '60vh' : 'auto',
              overflowY: authStore.isMobile ? 'auto' : 'visible'
            }}
          >
            <Avatar
              src={avatar}
              alt={fullName}
              style={{
                width: '100%',
                maxWidth: authStore.isMobile ? '100%' : '240px',
                borderRadius: 0,
                height: 'auto',
                objectFit: 'contain'
              }}
            />

            <Box
              style={{
                width: 'fit-content',
                display: 'flex',
                flexDirection: 'column',
                padding: 8,
                gap: 1,
                alignItems: 'flex-start'
              }}
            >
              <Typography variant="subtitle1">{fullName}</Typography>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  flexDirection: authStore.isMobile ? 'column' : 'row'
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: authStore.isMobile ? '100%' : 240,
                    gap: 0.5
                  }}
                >
                  <FieldInfoNG label={'Дата рождения:'} isVertical>
                    <Typography style={{ fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                      {getFomattedDate(details?.birthDate)}
                    </Typography>
                  </FieldInfoNG>
                  <FieldInfoNG label={'Адрес проживания:'} isVertical>
                    <Typography style={{ fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                      {details?.address || details?.residenceAddress || address || '-'}
                    </Typography>
                  </FieldInfoNG>
                  <FieldInfoNG label={`${details?.documentType?.name}:`} isVertical>
                    <Typography style={{ fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                      {details?.documentNumber || '-'}
                    </Typography>
                  </FieldInfoNG>
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: authStore.isMobile ? '100%' : 240,
                    gap: 0.5
                  }}
                >
                  <FieldInfoNG label={'ИНН:'} isVertical>
                    <Typography style={{ fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                      {details?.iin || '-'}
                    </Typography>
                  </FieldInfoNG>
                  <FieldInfoNG label={'Действителен:'} isVertical>
                    <Typography style={{ fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                      {getFomattedDate(details?.documentExpiryDate)}
                    </Typography>
                  </FieldInfoNG>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Popover>
    </>
  )
}

export default UserInfo
